import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  deleteEnquiry,
  searchEnquiries,
  updateEnquiry,
} from "../../features/enquiry/enquirySlice";

const EnquiryModal = ({ enquiry, type, onClose, loading }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: "",
    emailAdd: "",
    phoneNo: "",
    isBNIMember: "0",
    chapterName: "",
    interestedIn: "",
    bookingDate: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (enquiry) {
      setFormData({
        name: enquiry.name || "",
        emailAdd: enquiry.emailAdd || "",
        phoneNo: enquiry.phoneNo || "",
        isBNIMember: enquiry.isBNIMember || "0",
        chapterName: enquiry.chapterName || "",
        interestedIn: enquiry.interestedIn || "",
        bookingDate: enquiry.bookingDate
          ? enquiry.bookingDate.split("T")[0]
          : "",
      });
    }
  }, [enquiry]);

  const interestedInOptions = [
    { value: "", label: "Select..." },
    { value: "Podcast Recording", label: "Podcast Recording" },
    { value: "Cyclorama Wall", label: "Cyclorama Wall" },
    { value: "UGC / Content Creation", label: "UGC / Content Creation" },
    { value: "Commercial Shoot", label: "Commercial Shoot" },
    { value: "green screen zone", label: "Green Screen Zone" },
    { value: "Multi-Color Backdrop", label: "Multi-Color Backdrop" },
    { value: "other", label: "Other" },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.emailAdd.trim()) {
      newErrors.emailAdd = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.emailAdd)) {
      newErrors.emailAdd = "Email is invalid";
    }

    if (!formData.phoneNo.trim()) {
      newErrors.phoneNo = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNo)) {
      newErrors.phoneNo = "Phone number must be 10 digits";
    }

    if (!formData.interestedIn) {
      newErrors.interestedIn = "Please select an interest";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const updateData = {
        ...formData,
        bookingDate: formData.bookingDate
          ? `${formData.bookingDate}T00:00`
          : null,
      };

      await dispatch(
        updateEnquiry({
          id: enquiry.pkEnquiryDetailId,
          data: updateData,
        }),
      ).unwrap();

      toast.success("Enquiry updated successfully!", {
        position: "top-right",
        autoClose: 1000,
      });
      onClose();
      dispatch(searchEnquiries({ pageSize: "10", pageNo: "1" }));
    } catch (error) {
      toast.error("Failed to update enquiry", {
        position: "top-right",
        autoClose: 1000,
      });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this enquiry?")) {
      return;
    }

    try {
      await dispatch(deleteEnquiry(enquiry.pkEnquiryDetailId)).unwrap();
      toast.success("Enquiry deleted successfully!", {
        position: "top-right",
        autoClose: 1000,
      });
      onClose();
    } catch (error) {
      toast.error("Failed to delete enquiry", {
        position: "top-right",
        autoClose: 1000,
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (type === "view") {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white d-flex justify-content-between">
            <h5 className="modal-title">
              View Enquiry - ID: {enquiry.pkEnquiryDetailId}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Name</label>
                <p className="text-truncate">{enquiry.name}</p>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Email</label>
                <p className="text-truncate" title={enquiry.emailAdd}>
                  {enquiry.emailAdd}
                </p>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Phone</label>
                <p>{enquiry.phoneNo}</p>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">BNI Member</label>
                <p>
                  <span
                    className={`badge ${
                      enquiry.isBNIMember === "1"
                        ? "bg-success"
                        : "bg-secondary"
                    }`}
                  >
                    {enquiry.isBNIMember === "1" ? "Yes" : "No"}
                  </span>
                </p>
              </div>

              {enquiry.chapterName && (
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Chapter Name</label>
                  <p className="text-truncate" title={enquiry.chapterName}>
                    {enquiry.chapterName}
                  </p>
                </div>
              )}

              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Booking Date</label>
                <div className="d-flex align-items-center">
                  <i className="bi bi-calendar-event me-2 text-primary"></i>
                  <p className="mb-0">{formatDate(enquiry.bookingDate)}</p>
                </div>
              </div>

              <div className="col-12 mb-3">
                <label className="form-label fw-bold">Interested In</label>
                <div className="card">
                  <div className="card-body p-3">
                    <div className="d-flex flex-wrap gap-2">
                      {enquiry.interestedIn
                        ?.split(",")
                        .map((interest, index) => (
                          <span
                            key={index}
                            className="badge bg-primary px-3 py-2 d-flex align-items-center"
                            style={{ fontSize: "0.9rem" }}
                          >
                            <i className="bi bi-check-circle-fill me-2"></i>
                            {interest.trim()}
                          </span>
                        ))}
                    </div>

                    <div className="mt-3 pt-2 border-top">
                      <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        Total {enquiry.interestedIn?.split(",").length ||
                          0}{" "}
                        interests selected
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Created Date</label>
                <div className="d-flex align-items-center">
                  <i className="bi bi-calendar-plus me-2 text-success"></i>
                  <p className="mb-0">{formatDate(enquiry.createdDate)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === "edit") {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header bg-warning text-white">
            <h5 className="modal-title">
              Edit Enquiry - ID: {enquiry.pkEnquiryDetailId}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
            ></button>
          </div>
          <form onSubmit={handleUpdate}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    className={`form-control ${
                      errors.emailAdd ? "is-invalid" : ""
                    }`}
                    name="emailAdd"
                    value={formData.emailAdd}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.emailAdd && (
                    <div className="invalid-feedback">{errors.emailAdd}</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Phone *</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.phoneNo ? "is-invalid" : ""
                    }`}
                    name="phoneNo"
                    value={formData.phoneNo}
                    onChange={handleChange}
                    disabled={loading}
                    maxLength="10"
                  />
                  {errors.phoneNo && (
                    <div className="invalid-feedback">{errors.phoneNo}</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">BNI Member</label>
                  <select
                    className="form-select"
                    name="isBNIMember"
                    value={formData.isBNIMember}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="0">No</option>
                    <option value="1">Yes</option>
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Chapter Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="chapterName"
                    value={formData.chapterName}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Interested In *</label>
                  <select
                    className={`form-select ${
                      errors.interestedIn ? "is-invalid" : ""
                    }`}
                    name="interestedIn"
                    value={formData.interestedIn}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {interestedInOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.interestedIn && (
                    <div className="invalid-feedback">
                      {errors.interestedIn}
                    </div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Booking Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="bookingDate"
                    value={formData.bookingDate}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-warning text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Updating...
                  </>
                ) : (
                  "Update"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (type === "delete") {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header bg-danger text-white">
            <h5 className="modal-title">Delete Enquiry</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Are you sure you want to delete this enquiry?
            </div>
            <div className="card">
              <div className="card-body">
                <p className="mb-1">
                  <strong>ID:</strong> #{enquiry.pkEnquiryDetailId}
                </p>
                <p className="mb-1">
                  <strong>Name:</strong> {enquiry.name}
                </p>
                <p className="mb-1">
                  <strong>Email:</strong> {enquiry.emailAdd}
                </p>
                <p className="mb-1">
                  <strong>Phone:</strong> {enquiry.phoneNo}
                </p>
                <p className="mb-1">
                  <strong>Chapter Name:</strong> {enquiry.chapterName}
                </p>
                <p className="mb-0">
                  <strong>Interested In:</strong> {enquiry.interestedIn}
                </p>
              </div>
            </div>
            <p className="text-danger mt-3">
              <small>This action cannot be undone.</small>
            </p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default EnquiryModal;
